import React from 'react';
import { cn } from '../../lib/utils';
import { useI18n } from '../../i18n';

/** Responsive mark + wordmark presets (height scales with viewport). */
const MARK_SIZES = {
  sm: 'h-9 w-auto sm:h-10',
  md: 'h-11 w-auto sm:h-12',
  lg: 'h-12 w-auto sm:h-14 md:h-[3.75rem]',
  xl: 'h-14 w-auto sm:h-16 md:h-[4.25rem]',
  icon: 'h-10 w-10 sm:h-11 sm:w-11',
};

const WORDMARK_TITLE = {
  sm: 'text-base sm:text-lg',
  md: 'text-lg sm:text-xl',
  lg: 'text-xl sm:text-2xl',
  xl: 'text-2xl sm:text-3xl',
  icon: 'text-lg',
};

const WORDMARK_TAGLINE = {
  sm: 'text-[8px] sm:text-[9px]',
  md: 'text-[9px] sm:text-[10px]',
  lg: 'text-[10px] sm:text-xs',
  xl: 'text-[10px] sm:text-xs',
  icon: 'text-[9px]',
};

function LogoWordmark({ showText, textClassName, textSize }) {
  const { t } = useI18n();
  if (!showText) return null;
  return (
    <div className={cn('min-w-0', textClassName)}>
      <span
        className={cn(
          'font-manrope font-bold leading-none tracking-tight',
          WORDMARK_TITLE[textSize] || WORDMARK_TITLE.md,
        )}
      >
        {t('brand.name')}
      </span>
      <span
        className={cn(
          'block tracking-wider opacity-60 font-medium',
          WORDMARK_TAGLINE[textSize] || WORDMARK_TAGLINE.md,
        )}
      >
        {t('brand.tagline')}
      </span>
    </div>
  );
}

function resolveMarkClass(size, className) {
  return cn(MARK_SIZES[size] || MARK_SIZES.md, className);
}

const DomioLogoVector = ({
  className,
  size = 'md',
  showText = false,
  textClassName = '',
  trailing = null,
}) => (
  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
    <svg
      className={resolveMarkClass(size, className)}
      viewBox="0 0 100 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="houseGrad" x1="50" y1="0" x2="50" y2="75" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#1f84e6" />
          <stop offset="100%" stopColor="#005ca8" />
        </linearGradient>
        <linearGradient id="globeGrad" x1="20" y1="45" x2="80" y2="115" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#69d779" />
          <stop offset="40%" stopColor="#38b19b" />
          <stop offset="100%" stopColor="#005ca8" />
        </linearGradient>
      </defs>
      <path
        d="M50 42 C22 48 10 72 14 95 C18 112 32 120 50 120 C68 120 82 112 86 95 C90 72 78 48 50 42Z"
        fill="url(#globeGrad)"
      />
      <path
        d="M22 78 C35 65 55 62 75 72"
        stroke="#071E35"
        strokeWidth="4.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M18 92 C38 78 62 80 84 95"
        stroke="#071E35"
        strokeWidth="4.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M38 50 C30 72 32 95 42 115"
        stroke="#071E35"
        strokeWidth="4.5"
        fill="none"
        strokeLinecap="round"
      />
      <path d="M50 2 L18 35 L18 70 L82 70 L82 35 Z" fill="url(#houseGrad)" />
      <path d="M50 2 L12 40 L18 40 L50 10 L82 40 L88 40 Z" fill="url(#houseGrad)" />
      <rect x="39" y="33" width="22" height="22" rx="1.5" fill="#071E35" opacity="0.35" />
      <rect x="40" y="34" width="9" height="9" rx="1" fill="white" opacity="0.85" />
      <rect x="51" y="34" width="9" height="9" rx="1" fill="white" opacity="0.85" />
      <rect x="40" y="45" width="9" height="9" rx="1" fill="white" opacity="0.85" />
      <rect x="51" y="45" width="9" height="9" rx="1" fill="white" opacity="0.85" />
    </svg>
    {showText ? (
      <div className="flex items-center gap-2 min-w-0">
        <LogoWordmark showText textClassName={textClassName} textSize={size} />
        {trailing}
      </div>
    ) : (
      trailing
    )}
  </div>
);

export const DomioLogoPNG = ({
  className,
  size = 'md',
  showText = false,
  textClassName = '',
  trailing = null,
}) => (
  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
    <img
      src="/domio-logo.png"
      alt="Domio"
      className={cn(resolveMarkClass(size, className), 'object-contain shrink-0')}
    />
    {showText ? (
      <div className="flex items-center gap-2 min-w-0">
        <LogoWordmark showText textClassName={textClassName} textSize={size} />
        {trailing}
      </div>
    ) : (
      trailing
    )}
  </div>
);

export default DomioLogoPNG;
export { DomioLogoVector };
